import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Save, X, Tags, Sparkles, Loader2, Globe, Key, Copy, Plus } from 'lucide-react';
import { RichTextEditor } from '../../ContentEditors';

// Curated theme vocabulary — mirrors backend `COURSE_THEMES`.
// If you add/remove one here you MUST also update
// /app/backend/lms_models.py so the picker + filter stay in sync.
const CURATED_THEMES = [
  { key: 'beginner',  label: 'Beginner',  hint: 'Intro / fundamentals' },
  { key: 'business',  label: 'Business',  hint: 'Sales, ops, cottage-food' },
  { key: 'equipment', label: 'Equipment', hint: 'Machine setup + maintenance' },
  { key: 'recipes',   label: 'Recipes',   hint: 'Specific food, batch guides' },
  { key: 'nutrition', label: 'Nutrition', hint: 'Diet, storage, macros' },
  { key: 'safety',    label: 'Safety',    hint: 'Food safety, allergens, labeling' },
];

/**
 * AccessCodesPanel — inline widget that lists unused access codes for
 * a course and lets the admin mint a new one without leaving the
 * course editor. Mirrors the mint payload used by CourseCard so codes
 * mint through the same content-access audit trail. Redeemed / revoked
 * codes are filtered server-side (`status=unused`) so this list stays
 * short and actionable.
 */
const AccessCodesPanel = ({ courseId, courseTitle }) => {
  const { API } = useContext(AuthContext);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [minting, setMinting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/content-codes`, {
        params: { target_type: 'course', target_id: courseId, status: 'unused', limit: 50 },
      });
      setCodes(Array.isArray(data?.codes) ? data.codes : []);
    } catch (e) {
      // Silent — panel just stays empty if the admin can't list. The
      // Mint button still works so they can create a new one.
      setCodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (courseId) load(); /* eslint-disable-next-line */ }, [courseId]);

  const mintOne = async () => {
    setMinting(true);
    try {
      const { data } = await axios.post(`${API}/content-codes/generate`, {
        target_type:     'course',
        target_id:       courseId,
        target_label:    courseTitle,
        count:           1,
        grant_days:      365,
        max_redemptions: 1,
        note:            `Minted from Course editor for "${courseTitle}"`,
      });
      const newCode = data?.codes?.[0]?.code;
      if (newCode) {
        try { await navigator.clipboard.writeText(newCode); } catch { /* clipboard blocked */ }
        toast.success(`Code ${newCode} generated · copied to clipboard · valid 365 days`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mint code');
    } finally {
      setMinting(false);
    }
  };

  const copyOne = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      window.prompt('Copy this access code:', code);
    }
  };

  return (
    <div
      className="md:col-span-2 rounded-lg border border-amber-300/50 bg-amber-500/5 p-3 space-y-2"
      data-testid="access-codes-panel"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-sm">Access codes for this course</span>
          <span className="text-xs text-muted-foreground">
            {loading ? 'Loading…' : `${codes.length} unused`}
          </span>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={mintOne}
          disabled={minting}
          data-testid="mint-access-code-btn"
          className="bg-amber-500 hover:bg-amber-600 text-white"
        >
          {minting
            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Minting…</>
            : <><Plus className="w-3 h-3 mr-1" /> Mint new code</>}
        </Button>
      </div>
      {!loading && codes.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No unused codes yet. Click Mint to generate one — it'll be copied to your clipboard.
        </p>
      )}
      {codes.length > 0 && (
        <ul className="space-y-1" data-testid="access-codes-list">
          {codes.map((c) => (
            <li
              key={c.code}
              className="flex items-center justify-between gap-2 rounded-md bg-background/60 border border-amber-300/30 px-2.5 py-1.5"
              data-testid={`access-code-row-${c.code}`}
            >
              <div className="min-w-0 flex-1">
                <code className="font-mono text-sm font-semibold text-amber-800 dark:text-amber-300">
                  {c.code}
                </code>
                <div className="text-[10px] text-muted-foreground">
                  {c.grant_days ? `${c.grant_days}d access` : 'Access'} · max redemptions {c.max_redemptions || 1}
                  {c.note ? ` · ${c.note}` : ''}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => copyOne(c.code)}
                data-testid={`access-code-copy-${c.code}`}
                title="Copy code to clipboard"
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CourseForm = ({
 courseForm,
 setCourseForm,
 editingCourse,
 onSave,
 onCancel,
 onOpenResearch,
}) => {
 const { API } = React.useContext(AuthContext);
 const [drafting, setDrafting] = React.useState(false);

 const draftAbstract = async () => {
   if (!editingCourse?.id) {
     toast.error('Save the course first, then auto-draft the abstract.');
     return;
   }
   setDrafting(true);
   try {
     const { data } = await axios.post(
       `${API}/admin/lms/courses/${editingCourse.id}/generate-abstract`,
     );
     setCourseForm({ ...courseForm, abstract: data.abstract || '' });
     toast.success('Abstract drafted. Edit before saving.');
   } catch (e) {
     const msg = e?.response?.data?.detail;
     toast.error(typeof msg === 'string' ? msg : 'AI draft failed.');
   } finally {
     setDrafting(false);
   }
 };

 return (
 <Card className="mb-6">
 <CardHeader>
 <CardTitle>{editingCourse ? 'Edit Course' : 'Create New Course'}</CardTitle>
 <CardDescription>
 {editingCourse ? 'Update course details' : 'Create a new course for your students'}
 </CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-2 gap-4">
 <div>
 <Label>Course Title</Label>
 <Input
 value={courseForm.title}
 onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
 placeholder="Introduction to Freeze Drying"
 />
 </div>
 <div>
 <Label>Instructor</Label>
 <Input
 value={courseForm.instructor}
 onChange={(e) => setCourseForm({ ...courseForm, instructor: e.target.value })}
 placeholder="John Doe"
 />
 </div>
 <div className="md:col-span-2">
 <Label>Description</Label>
 <RichTextEditor
 value={courseForm.description}
 onChange={(value) => setCourseForm({ ...courseForm, description: value })}
 placeholder="Course description..."
 />
 </div>
 {/* AI-facing course abstract — drives the Research assistant */}
 <div className="md:col-span-2 rounded-lg border border-blue-300/40 bg-blue-500/5 p-3">
 <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
 <Label className="flex items-center gap-1.5 font-semibold">
 <Sparkles className="w-4 h-4 text-blue-500" />
 AI Research Abstract
 <span className="text-xs text-muted-foreground font-normal">
 — 2-3 paragraph summary the AI uses to search the KB and the web
 </span>
 </Label>
 <div className="flex gap-2">
 {editingCourse && (
   <Button
     type="button"
     size="sm"
     variant="outline"
     onClick={draftAbstract}
     disabled={drafting}
     data-testid="course-abstract-draft-btn"
     title="Ask the AI to draft an abstract from this course's modules and lessons"
   >
     {drafting ? (
       <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Drafting…</>
     ) : (
       <><Sparkles className="w-4 h-4 mr-1" /> Auto-draft with AI</>
     )}
   </Button>
 )}
 {editingCourse && onOpenResearch && (
   <Button
     type="button"
     size="sm"
     variant="outline"
     onClick={onOpenResearch}
     data-testid="course-open-research-btn"
     title="Open the KB-first research assistant for this course"
   >
     <Globe className="w-4 h-4 mr-1" /> Research
   </Button>
 )}
 </div>
 </div>
 <textarea
   value={courseForm.abstract || ''}
   onChange={(e) => setCourseForm({ ...courseForm, abstract: e.target.value })}
   placeholder={editingCourse
     ? 'Click "Auto-draft with AI" or write 2-3 paragraphs describing what this course teaches and the topics worth further research.'
     : 'Save the course first, then use "Auto-draft with AI" to generate an abstract from your modules.'}
   className="w-full p-3 border border-input rounded-md min-h-[120px] text-sm bg-background"
   data-testid="course-abstract-textarea"
 />
 <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
   <input
     type="checkbox"
     checked={!!courseForm.web_enrichment_enabled}
     onChange={(e) => setCourseForm({ ...courseForm, web_enrichment_enabled: e.target.checked })}
     className="w-4 h-4"
     data-testid="course-web-enrichment-toggle"
   />
   <span>
     <span className="font-medium">Allow live web enrichment</span>
     <span className="block text-xs text-muted-foreground font-normal">
       KB is always searched first. Web is only used when the KB can't
       confidently answer a course question.
     </span>
   </span>
 </label>
 </div>
 <div>
 <Label>Tier</Label>
 <select
 value={courseForm.tier}
 onChange={(e) => setCourseForm({ ...courseForm, tier: e.target.value })}
 className="w-full p-2 border rounded-lg"
 >
 <option value="free">Free</option>
 <option value="pro">Pro</option>
 <option value="business">Business</option>
 <option value="enterprise">Enterprise</option>
 <option value="specialty">Specialty</option>
 <option value="paid">Certificate (paid)</option>
 </select>
 </div>
 <div>
 <Label>Duration (minutes)</Label>
 <Input
 type="number"
 value={courseForm.duration_minutes}
 onChange={(e) => setCourseForm({ ...courseForm, duration_minutes: e.target.value })}
 placeholder="60"
 />
 </div>
 <div>
 <Label>Thumbnail URL (optional)</Label>
 <Input
 value={courseForm.thumbnail_url}
 onChange={(e) => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 <div>
 <Label>Order</Label>
 <Input
 type="number"
 value={courseForm.order}
 onChange={(e) => setCourseForm({ ...courseForm, order: parseInt(e.target.value) })}
 />
 </div>
 <div className="flex items-center gap-2">
 <input
 type="checkbox"
 checked={courseForm.is_published}
 onChange={(e) => setCourseForm({ ...courseForm, is_published: e.target.checked })}
 className="w-4 h-4"
 />
 <Label>Published</Label>
 </div>
 <div className="md:col-span-2">
 <Label>Visibility</Label>
 <select
 value={courseForm.visibility || 'public'}
 onChange={(e) => setCourseForm({ ...courseForm, visibility: e.target.value })}
 className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
 data-testid="course-visibility-select"
 >
 <option value="public">Public — listed in the catalog</option>
 <option value="unlisted">Unlisted — accessible via direct link only (work-in-progress)</option>
 <option value="private">Private — invited members only (works with access codes)</option>
 </select>
 </div>
 <div className="flex items-center gap-2 md:col-span-2 rounded-lg border border-amber-300/40 bg-amber-500/5 px-3 py-2">
 <input
 type="checkbox"
 checked={!!courseForm.access_code_required}
 onChange={(e) => setCourseForm({ ...courseForm, access_code_required: e.target.checked })}
 className="w-4 h-4"
 data-testid="course-access-code-required"
 />
 <Label className="cursor-pointer">
 <span className="font-semibold">Require access code to enroll</span>
 <span className="block text-xs text-muted-foreground font-normal">
 When enabled, members must redeem a content code (mint them on the Access Codes admin page) to see the modules.
 </span>
 </Label>
 </div>

 {/* Inline access-code panel — surfaces existing unused codes for
     THIS course and gives admins a one-click "mint another"
     button right under the toggle so they never have to hunt for
     the Access Codes admin page. Rendered only when the toggle is
     on AND the course has been saved once (needs an id). */}
 {courseForm.access_code_required && editingCourse?.id && (
   <AccessCodesPanel
     courseId={editingCourse.id}
     courseTitle={courseForm.title || editingCourse.title || 'Untitled course'}
   />
 )}
 {courseForm.access_code_required && !editingCourse?.id && (
   <div className="md:col-span-2 rounded-lg border border-dashed border-amber-300/50 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground">
     Save the course first — codes are minted against the course id, so we need to persist it before generating any.
   </div>
 )}
 <div className="md:col-span-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
 <Label className="flex items-center gap-1.5 mb-2 font-semibold">
 <Tags className="w-4 h-4" /> Themes
 <span className="text-xs text-muted-foreground font-normal">
 — pick every theme this course fits so learners can find it on the catalog
 </span>
 </Label>
 <div className="flex flex-wrap gap-2" data-testid="course-themes-picker">
 {CURATED_THEMES.map((t) => {
 const selected = (courseForm.themes || []).includes(t.key);
 return (
 <button
 type="button"
 key={t.key}
 onClick={() => {
 const cur = new Set(courseForm.themes || []);
 if (cur.has(t.key)) cur.delete(t.key); else cur.add(t.key);
 setCourseForm({ ...courseForm, themes: Array.from(cur) });
 }}
 title={t.hint}
 data-testid={`theme-chip-${t.key}`}
 className={
 'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-colors ' +
 (selected
 ? 'bg-primary text-primary-foreground border-primary'
 : 'bg-background text-foreground border-border hover:border-primary/50')
 }
 >
 {t.label}
 </button>
 );
 })}
 </div>
 {!(courseForm.themes || []).length && (
 <p className="text-[11px] text-muted-foreground mt-2">
 No themes selected yet. Pick at least one so the store filter can catch it.
 </p>
 )}
 </div>
 </div>

 {/* Advertising Section */}
 <div className="md:col-span-2 border-t pt-4 mt-4">
 <h4 className="font-semibold mb-3 flex items-center gap-2">
 📢 Advertisement Settings
 </h4>
 <div className="space-y-3">
 <div className="flex items-center gap-2">
 <input
 type="checkbox"
 checked={courseForm.ad_enabled || false}
 onChange={(e) => setCourseForm({ ...courseForm, ad_enabled: e.target.checked })}
 className="w-4 h-4"
 />
 <Label>Enable Advertisement for this Course</Label>
 </div>
 
 {courseForm.ad_enabled && (
 <>
 <div>
 <Label>Ad Title</Label>
 <Input
 value={courseForm.ad_title || ''}
 onChange={(e) => setCourseForm({ ...courseForm, ad_title: e.target.value })}
 placeholder="Unlock Premium Features!"
 />
 </div>
 <div>
 <Label>Ad Content</Label>
 <textarea
 value={courseForm.ad_content || ''}
 onChange={(e) => setCourseForm({ ...courseForm, ad_content: e.target.value })}
 placeholder="Get access to advanced courses and exclusive content..."
 className="w-full p-2 border rounded-lg min-h-[60px]"
 />
 </div>
 <div>
 <Label>Ad Image URL (optional)</Label>
 <Input
 value={courseForm.ad_image_url || ''}
 onChange={(e) => setCourseForm({ ...courseForm, ad_image_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 <div>
 <Label>Ad Link URL</Label>
 <Input
 value={courseForm.ad_link_url || ''}
 onChange={(e) => setCourseForm({ ...courseForm, ad_link_url: e.target.value })}
 placeholder="https://..."
 />
 </div>
 <div>
 <Label>Ad Link Text</Label>
 <Input
 value={courseForm.ad_link_text || ''}
 onChange={(e) => setCourseForm({ ...courseForm, ad_link_text: e.target.value })}
 placeholder="Learn More"
 />
 </div>
 </>
 )}
 </div>
 </div>

 <div className="flex gap-3 mt-4">
 <Button onClick={onSave} className="bg-blue-600 hover:bg-blue-700">
 <Save className="w-4 h-4 mr-2" />
 {editingCourse ? 'Update Course' : 'Create Course'}
 </Button>
 <Button onClick={onCancel} variant="outline">
 <X className="w-4 h-4 mr-2" />
 Cancel
 </Button>
 </div>
 </CardContent>
 </Card>
 );
};

export default CourseForm;
